import type { ResourceDryRunChange, ResourceDryRunResponse } from "@k8s-dashboard/contracts";
import { fixedTextFor } from "./dryrun";

/**
 * 검토 결과 렌더 (ADR 0019)
 *
 * ChangeReviewPanel.tsx에서 **옮겨 온 블록**입니다. 내용은 그대로이고 위치만
 * 바뀌었습니다 — 패널이 적용 단계까지 맡게 되면서 한 파일 안에 두기에 길어졌습니다.
 *
 * 모르는 값은 그대로 되비추지 않습니다. 서버가 무엇을 보내든 DOM에 나가는 문자열은
 * 이 파일 안의 표에 있는 것뿐이고, 텍스트든 속성이든 같습니다.
 */

/* 계약 상한과 같은 값입니다. 서버가 더 보낼 수 없지만, 화면은 그것을 믿고
   무한히 그리지 않습니다 — 방어적으로 한 번 더 자릅니다. */
const MAX_CHANGES = 200;
const MAX_WARNINGS = 32;
const MAX_VIOLATIONS = 32;
const MAX_REDACTED = 64;

const OUTCOME: Record<string, { glyph: string; label: string; detail: string }> = {
  unchanged: { glyph: "=", label: "변경 없음", detail: "예상 변경이 없습니다." },
  changed: { glyph: "±", label: "변경 있음", detail: "아래가 예상 변경입니다." },
  rejected: { glyph: "!", label: "거절됨", detail: "서버가 이 변경을 받아들이지 않았습니다." },
};

const UNKNOWN_OUTCOME = {
  glyph: "?",
  label: "결과를 해석할 수 없습니다",
  detail: "서버가 이 화면이 모르는 형식으로 답했습니다.",
};

/** data 속성에도 알려진 값 또는 "unknown"만 들어갑니다. */
const UNKNOWN_OUTCOME_ATTR = "unknown";

const REJECTED_BY: Record<string, string> = {
  validation: "서버 검증에서 거절",
  admission: "admission webhook이 거절",
  conflict: "다른 field manager와 소유권 충돌",
};

const UNKNOWN_REJECTED_BY = "알 수 없는 사유";

const OP_LABEL: Record<string, string> = { added: "추가", removed: "제거", changed: "변경" };

const UNKNOWN_OP = "알 수 없음";

export /** 결과 렌더는 계약 상한 안에서만 그립니다. 색만으로 상태를 전달하지 않습니다. */
function ReviewResult({ result }: { result: ResourceDryRunResponse }) {
  /* 모르는 값은 표에 없는 문구로만 답합니다. 원문을 되비추지 않습니다. */
  const known = Object.prototype.hasOwnProperty.call(OUTCOME, result.outcome);
  const outcome = known ? OUTCOME[result.outcome] : UNKNOWN_OUTCOME;
  const outcomeAttr = known ? result.outcome : UNKNOWN_OUTCOME_ATTR;
  const changes = (result.changes ?? []).slice(0, MAX_CHANGES);
  const warnings = (result.warnings ?? []).slice(0, MAX_WARNINGS);
  const violations = (result.violations ?? []).slice(0, MAX_VIOLATIONS);
  const redacted = (result.redacted ?? []).slice(0, MAX_REDACTED);

  return (
    <section className="resource-review__result" aria-label="검토 결과">
      <p className="resource-review__outcome" data-outcome={outcomeAttr}>
        <span className="resource-review__glyph" aria-hidden="true">
          {outcome.glyph}
        </span>
        <strong>{outcome.label}</strong>
        <span className="muted"> {outcome.detail}</span>
        {result.rejectedBy && (
          <span className="resource-review__rejected">
            {" "}
            사유: {fixedTextFor(REJECTED_BY, result.rejectedBy) ?? UNKNOWN_REJECTED_BY}
          </span>
        )}
      </p>

      <p className="resource-review__count muted">
        변경 {result.changeCount}건
        {result.truncated ? ` · 목록은 ${changes.length}건까지만 표시합니다(잘림)` : ""}
      </p>

      {changes.length > 0 && (
        <div className="panel__scroll panel__scroll--fixed">
          <table className="ds-data-table ds-data-table--compact">
            <caption className="visually-hidden">예상 변경 목록</caption>
            <thead>
              <tr>
                <th>경로</th>
                <th className="resource-review__op-heading">종류</th>
                <th>값</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((change) => (
                <tr key={`${change.op}:${change.path}`}>
                  <td className="ds-ident">{change.path}</td>
                  <td>{fixedTextFor(OP_LABEL, change.op) ?? UNKNOWN_OP}</td>
                  <td>
                    <ChangeValue change={change} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {violations.length > 0 && (
        <div className="resource-review__list">
          <h3 className="resource-review__list-title">거절 사유 {violations.length}건</h3>
          <ul>
            {violations.map((violation, index) => (
              <li key={`violation-${index}`}>
                <span className="resource-review__glyph" aria-hidden="true">
                  !
                </span>
                {violation.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {warnings.length > 0 && (
        <div className="resource-review__list">
          <h3 className="resource-review__list-title">서버 경고 {warnings.length}건</h3>
          <ul>
            {warnings.map((warning, index) => (
              <li key={`warning-${index}`}>
                <span className="resource-review__glyph" aria-hidden="true">
                  ▲
                </span>
                {warning}
              </li>
            ))}
          </ul>
        </div>
      )}

      {redacted.length > 0 && (
        <p className="resource-review__redacted muted" role="note">
          비교에서 제외된 경로 {redacted.length}개: <span className="ds-ident">{redacted.join(", ")}</span>
        </p>
      )}
    </section>
  );
}

/**
 * 값 한 칸입니다.
 *
 * `valueRedacted`면 before/after를 **읽지도 않습니다.** 조건부 렌더링으로만 가리면
 * 나중에 누군가 툴팁이나 title 속성에 같은 값을 붙일 자리가 남습니다.
 */
function ChangeValue({ change }: { change: ResourceDryRunChange }) {
  if (change.valueRedacted) {
    return <span className="resource-review__redacted-value">값은 표시하지 않습니다</span>;
  }
  return (
    <span className="resource-review__value">
      <span className="ds-ident">{change.before ?? "-"}</span>
      <span aria-hidden="true"> → </span>
      <span className="visually-hidden">에서</span>
      <span className="ds-ident">{change.after ?? "-"}</span>
      <span className="visually-hidden">(으)로</span>
      {change.valueTruncated && <span className="resource-review__truncated"> (값 잘림)</span>}
    </span>
  );
}
